import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import * as refApi from '../../api/referrals.api';
import { errorMessage } from '../../api/client';

function ReferralAdmin() {
  const [analytics, setAnalytics] = useState(null);
  const [board, setBoard] = useState([]);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [a, b, l] = await Promise.all([
          refApi.adminAnalytics(),
          refApi.adminLeaderboard(),
          refApi.adminListReferrals(),
        ]);
        if (cancelled) return;
        setAnalytics(a); setBoard(b || []); setList(l || []);
      } catch (e) {
        toast.error(errorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  async function invalidate(id) {
    const reason = prompt('Why are you marking this referral invalid?');
    if (!reason) return;
    try {
      await refApi.adminInvalidate(id, reason);
      toast.success('Marked invalid');
      const fresh = await refApi.adminListReferrals();
      setList(fresh || []);
    } catch (e) { toast.error(errorMessage(e)); }
  }

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <PageHeader title="Referral analytics" subtitle="PRD §1.5 — acquisition program performance." />

      {analytics && (
        <div className="grid grid-4" style={{ gap: 'var(--s-3)' }}>
          <Card label="Total signups"     value={analytics.totalSignups} />
          <Card label="Conversions"       value={analytics.totalConversions} sub={`${analytics.conversionRate}% rate`} />
          <Card label="Total spend"       value={`MYR ${analytics.totalSpend}`} sub="all-time rewards paid" />
          <Card label="Approx CPA"        value={`MYR ${analytics.cpaApprox}`} sub="per conversion" />
        </div>
      )}

      <div className="card" style={{ marginTop: 'var(--s-4)' }}>
        <h3>Top referrers</h3>
        {board.length === 0 ? (
          <p className="muted">No referrers yet.</p>
        ) : (
          <table className="table">
            <thead><tr><th>#</th><th>User</th><th>Code</th><th>Signups</th><th>Conversions</th><th>Reward</th></tr></thead>
            <tbody>
              {board.map((r, i) => (
                <tr key={r._id}>
                  <td>{i + 1}</td>
                  <td>{r.user?.name} <span className="muted text-xs">({r.user?.email})</span></td>
                  <td><code>{r.user?.referralCode}</code></td>
                  <td>{r.signups}</td>
                  <td>{r.conversions}</td>
                  <td>MYR {r.reward || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card" style={{ marginTop: 'var(--s-4)' }}>
        <h3>All referrals</h3>
        {list.length === 0 ? (
          <p className="muted">None.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead><tr>
                <th>When</th><th>Referrer</th><th>Referred</th><th>Status</th><th>IP</th><th></th>
              </tr></thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r._id}>
                    <td>{new Date(r.createdAt).toLocaleString()}</td>
                    <td>{r.referrer?.name} <span className="muted text-xs">{r.referrer?.referralCode}</span></td>
                    <td>{r.referredUser?.name || r.referredUser?.email || <span className="muted">visitor</span>}</td>
                    <td><span className={r.status === 'INVALID' ? 'badge danger' : 'badge'}>{r.status}</span></td>
                    <td className="muted text-xs">{r.signupIp || r.clickIp}</td>
                    <td>
                      {r.status !== 'INVALID' && (
                        <button className="btn btn-link" onClick={() => invalidate(r._id)}>Invalidate</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function Card({ label, value, sub }) {
  return (
    <div className="card">
      <div className="muted text-xs" style={{ textTransform: 'uppercase', letterSpacing: '0.1em' }}>{label}</div>
      <div className="bold" style={{ fontSize: 22, marginTop: 6 }}>{value}</div>
      {sub && <div className="muted text-xs">{sub}</div>}
    </div>
  );
}

export default ReferralAdmin;
